import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BlueprintBackground } from "@/components/BlueprintBackground";
import { Logo } from "@/components/Logo";
import { Wordmark } from "@/components/Wordmark";
import { api } from "@/api/client";
import { toast } from "sonner";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. You can now log in.");
      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not reset your password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="reset-password-page">
        <BlueprintBackground />
        <div className="w-full max-w-sm rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-md p-8 shadow-2xl text-center">
          <p className="text-rose-400 text-sm mb-4" data-testid="reset-password-no-token">This reset link is missing or invalid.</p>
          <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300 font-medium text-sm">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="reset-password-page">
      <BlueprintBackground />
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Logo size={26} />
          <Wordmark className="font-heading text-xl font-bold tracking-tight text-white" />
        </Link>
        <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-md p-8 shadow-2xl">
          <h1 className="font-heading text-2xl font-bold text-white mb-1">Set a new password</h1>
          <p className="text-slate-400 text-sm mb-6">Choose a new password for your account.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-slate-300">New password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
                className="mt-1.5 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm password</Label>
              <Input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="reset-password-confirm-input"
                className="mt-1.5 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                placeholder="Retype your new password"
              />
            </div>
            {error && (
              <p className="text-sm text-rose-400" data-testid="reset-password-error-message">{error}</p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-password-submit-button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Reset Password
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
